import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, DatePicker, Switch, 
  Spin, Pagination, Card, Row, Col, Tag, Typography, Drawer, 
  Descriptions, Divider, App, Radio, Tooltip, Space, Popconfirm, Empty
} from 'antd';
import { Image } from 'antd';
import {
  PlayCircleOutlined, StopOutlined, DeleteOutlined, EyeOutlined,
  ReloadOutlined, PlusOutlined, ClockCircleOutlined, 
  FieldTimeOutlined, InfoCircleOutlined, CopyOutlined,
  CleanupOutlined, SearchOutlined, SyncOutlined, ClearOutlined,
  UpOutlined, DownOutlined, LinkOutlined, DisconnectOutlined, QrcodeOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import QRCode from 'qrcode';
import { 
  getVideoFiles, startStream, stopStream, getActiveStreams, 
  cleanupTempFiles, getTagOptions, enableExternalLink, closeExternalLink,
  fetchVisitorInfo, getVisitorInfo
} from '../../services/liveSimService';
import { getGroupList } from '../../services/plasoService';
import axiosInstance from '../../services/axiosInstance';
import { formatDuration } from '../../utils/formatUtils';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
let liveSimInitRequestPromise = null;

const LiveSimList = () => {
  const [activeStreams, setActiveStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 直播列表分页状态
  const [streamPage, setStreamPage] = useState(1);
  const [streamPageSize, setStreamPageSize] = useState(10);
  const [streamTotal, setStreamTotal] = useState(0);
  
  const [streamModalVisible, setStreamModalVisible] = useState(false);
  const [videoFiles, setVideoFiles] = useState([]);
  const [videoTotal, setVideoTotal] = useState(0);
  const [videoPage, setVideoPage] = useState(1);
  const [videoPageSize, setVideoPageSize] = useState(10);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [submittingTask, setSubmittingTask] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [streamForm] = Form.useForm();
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedStream, setSelectedStream] = useState(null);
  const [calculatedEndTime, setCalculatedEndTime] = useState(null);
  const [canSubmit, setCanSubmit] = useState(false);
  
  const [tagOptions, setTagOptions] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]); // 改为多选
  const [groupPagination, setGroupPagination] = useState({
    current: 0,
    hasMore: true,
    total: 0
  });
  const [currentSearchKey, setCurrentSearchKey] = useState(''); // 当前搜索关键词
  const [filterExpanded, setFilterExpanded] = useState(false); // 筛选框展开状态
  
  // 二维码相关状态
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  
  // 潜在学生相关状态
  const [visitorInfo, setVisitorInfo] = useState([]);
  const [visitorLoading, setVisitorLoading] = useState(false);
  const [fetchingVisitorInfo, setFetchingVisitorInfo] = useState(false);
  
  // 使用App.useApp获取messageApi
  const { message: messageApi } = App.useApp();
  
  // 筛选条件 - 增加标签筛选
  const [filters, setFilters] = useState({
    grade_name: undefined,
    class_name: undefined,
    course_name: undefined,
    teacher_name: '',
    searchText: '',
    custom_tag: undefined  // 新增：标签筛选
  });

  // 届次选项
  const gradeOptions = [
    { value: '2025届', label: '2025届' },
    { value: '2026届', label: '2026届' },
    { value: '2027届', label: '2027届' },
    { value: '2028届', label: '2028届' },
    { value: '测试届次', label: '测试届次' },
  ];
  
  // 班次选项
  const classOptions = [
    { value: '全程班', label: '全程班' },
    { value: '暑假班', label: '暑假班' },
    { value: '寒假班', label: '寒假班' },
    { value: '点题班', label: '点题班' },
    { value: '全日制班', label: '全日制班' },
    { value: '测试班次', label: '测试班次' },
  ];
  
  // 课程选项
  const courseOptions = [
    { value: '高等数学', label: '高等数学' },
    { value: '大学语文', label: '大学语文' },
    { value: '英语四级等级考试', label: '英语四级等级考试' },
    { value: '日语四级等级考试', label: '日语四级等级考试' },
    { value: '计算机一级等级考试', label: '计算机一级等级考试' },
    { value: '管理类', label: '管理类' },
    { value: '财经类', label: '财经类' },
    { value: '电子信息类', label: '电子信息类' },
    { value: '化工生物类', label: '化工生物类' },
    { value: '机械工程类', label: '机械工程类' },
    { value: '土木建筑类', label: '土木建筑类' },
    { value: '计算机类', label: '计算机类' },
    { value: '美术设计类', label: '美术设计类' },
    { value: '环境资源类', label: '环境资源类' },
    { value: '教育类', label: '教育类' },
    { value: '农林类', label: '农林类' },
    { value: '英语类', label: '英语类' },
    { value: '新闻传播类', label: '新闻传播类' },
    { value: '音乐类', label: '音乐类' },
    { value: '日语类', label: '日语类' },
    { value: '法学类', label: '法学类' },
    { value: '食品类', label: '食品类' },
    { value: '文史类', label: '文史类' },
    { value: '医护类', label: '医护类' },
    { value: '测试课程', label: '测试课程' },
  ];

  // 新增：获取标签列表
  const fetchTagOptions = async () => {
    setTagLoading(true);
    try {
      const response = await axiosInstance.get('plaso/custom_tags');
      
      if (response && response.code === 0) {
        const tagsData = response.data;
        
        // 检查是否是包装后的格式（有items字段）
        if (tagsData && Array.isArray(tagsData.items)) {
          const options = tagsData.items.map(tag => ({ value: tag.tag_name, label: tag.tag_name }));
          setTagOptions(options);
        } 
        // 检查是否是直接的数组格式
        else if (Array.isArray(tagsData)) {
          const options = tagsData.map(tag => ({ value: tag.tag_name, label: tag.tag_name }));
          setTagOptions(options);
        } else {
          console.warn('标签数据格式异常:', tagsData);
          setTagOptions([]);
        }
      } else {
        console.warn('标签接口返回格式异常:', response);
        setTagOptions([]);
      }
    } catch (error) {
      console.error('获取标签列表失败:', error);
      messageApi.error('获取标签列表失败');
      setTagOptions([]);
    } finally {
      setTagLoading(false);
    }
  };

  // 新增：获取班级列表
  const fetchGroupOptions = async (searchKey = '', loadMore = false) => {
    setGroupLoading(true);
    try {
      // 如果是新搜索，重置分页
      const pageStart = loadMore ? groupPagination.current + 1 : 0;
      
      const response = await getGroupList(searchKey, pageStart, 50);
      
      if (response && response.code === 0) {
        const groupData = response.data;
        
        // 检查伯索API返回的数据结构
        if (groupData && groupData.obj && Array.isArray(groupData.obj.list)) {
          const newOptions = groupData.obj.list.map(group => ({
            value: group.id,
            label: `${group.groupName} (${group.teacherId})`,
            groupName: group.groupName,
            teacherId: group.teacherId,
            groupRemarks: group.groupRemarks,
            sCount: group.sCount,
            activeBegin: group.activeBegin,
            activeEnd: group.activeEnd
          }));
          
          // 如果是加载更多，追加数据；否则替换数据
          if (loadMore) {
            setGroupOptions(prev => [...prev, ...newOptions]);
          } else {
            setGroupOptions(newOptions);
          }
          
          // 更新分页信息
          setGroupPagination({
            current: pageStart,
            hasMore: groupData.obj.list.length === 50, // 如果返回的数据等于页面大小，说明可能还有更多数据
            total: groupData.obj.total || 0
          });
        } else {
          console.warn('班级数据格式异常:', groupData);
          if (!loadMore) {
            setGroupOptions([]);
            setGroupPagination({ current: 0, hasMore: false, total: 0 });
          }
        }
      } else {
        console.warn('班级接口返回格式异常:', response);
        if (!loadMore) {
          setGroupOptions([]);
          setGroupPagination({ current: 0, hasMore: false, total: 0 });
        }
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      messageApi.error('获取班级列表失败');
      if (!loadMore) {
        setGroupOptions([]);
        setGroupPagination({ current: 0, hasMore: false, total: 0 });
      }
    } finally {
      setGroupLoading(false);
    }
  };

  // 页面加载时获取活跃直播流数据和标签列表
  useEffect(() => {
    const runInitLoad = async () => {
      if (liveSimInitRequestPromise) {
        await liveSimInitRequestPromise;
        return;
      }

      liveSimInitRequestPromise = (async () => {
        await Promise.all([
          fetchActiveStreams(1, 10), // 页面加载时从第一页开始
          fetchTagOptions(),         // 获取标签列表
          fetchGroupOptions()        // 获取班级列表
        ]);
      })();

      try {
        await liveSimInitRequestPromise;
      } finally {
        liveSimInitRequestPromise = null;
      }
    };

    runInitLoad();
  }, []);

  // 监听筛选条件变化，自动刷新数据
  useEffect(() => {
    // 当筛选条件变化时，重置到第一页并获取数据
    setVideoPage(1);
    fetchVideoFiles(1, videoPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // 加载视频列表
  const fetchVideoFiles = async (page = videoPage, pageSize = videoPageSize) => {
    setLoadingVideos(true);
    try {
      // 构建筛选参数
      const activeFilters = { ...filters };
      
      // 删除undefined和空字符串值
      Object.keys(activeFilters).forEach(key => {
        if (activeFilters[key] === undefined || activeFilters[key] === '') {
          delete activeFilters[key];
        }
      });
      
      // 调用API
      const result = await getVideoFiles(activeFilters, { page, page_size: pageSize });
      
      // 更新状态
      setVideoFiles(result.items || []);
      setVideoTotal(result.total || 0);
      setVideoPage(page);
      setVideoPageSize(pageSize);
    } catch (error) {
      console.error('加载视频文件失败:', error);
      messageApi.error('加载视频文件失败: ' + (error.message || '未知错误'));
    } finally {
      setLoadingVideos(false);
    }
  };

  // 获取活跃直播流列表
  const fetchActiveStreams = async (page = streamPage, pageSize = streamPageSize) => {
    setLoading(true);
    try {
      const response = await getActiveStreams({ page, page_size: pageSize });
      
      if (response && response.code === 0) {
        // 处理分页响应格式
        const streamsData = response.data;
        
        if (streamsData && Array.isArray(streamsData.items)) {
          
          // 确保每个数据项都有唯一的id
          streamsData.items.forEach((stream, index) => {
            if (!stream.id) {
              console.warn(`警告: 直播项 #${index + 1} 没有id字段，将生成一个临时id`);
              stream.id = `temp_${index}_${Date.now()}`;
            }
          });
          
          // 设置直播流数据和分页信息
          setActiveStreams(streamsData.items);
          setStreamTotal(streamsData.total);
          setStreamPage(page);
          setStreamPageSize(pageSize);
        } else {
          console.warn('响应数据格式异常:', streamsData);
          setActiveStreams([]);
          setStreamTotal(0);
        }
      } else {
        console.warn('获取活跃直播流失败:', response);
        messageApi.error('获取活跃直播流列表失败');
        setActiveStreams([]);
        setStreamTotal(0);
      }
    } catch (error) {
      console.error('获取活跃直播流异常:', error);
      messageApi.error('获取活跃直播流列表失败');
      setActiveStreams([]);
      setStreamTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 处理手动刷新
  const handleManualRefresh = () => {
    fetchActiveStreams(streamPage, streamPageSize); // 保持当前分页状态
    messageApi.success('数据已刷新');
  };

  // 处理直播列表分页变化
  const handleStreamPageChange = (page, pageSize) => {
    setStreamPage(page);
    setStreamPageSize(pageSize);
    fetchActiveStreams(page, pageSize);
  };

  // 处理分页变化
  const handlePageChange = (page, pageSize) => {
    setVideoPage(page);
    setVideoPageSize(pageSize);
    fetchVideoFiles(page, pageSize);
  };

  // 处理筛选条件变化
  const handleFilterChange = (values) => {
    setFilters(prev => ({
      ...prev,
      ...values
    }));
    // 不在这里调用fetchVideoFiles，让useEffect处理
  };

  // 处理搜索文本变化
  const handleSearchChange = (value) => {
    setFilters(prev => ({
      ...prev,
      searchText: value
    }));
    // 不在这里调用fetchVideoFiles，让useEffect处理
  };

  // 应用筛选条件
  const handleApplyFilters = () => {
    fetchVideoFiles(1, videoPageSize);
  };

  // 重置筛选条件
  const handleResetFilters = () => {
    const resetFilters = {
      grade_name: undefined,
      class_name: undefined,
      course_name: undefined,
      teacher_name: '',
      searchText: '',
      custom_tag: undefined  // 新增：重置标签筛选
    };
    
    setFilters(resetFilters);
    streamForm.resetFields();
    
    // 重置条件后立即获取数据
    setVideoPage(1);
    fetchVideoFiles(1, videoPageSize);
  };

  // 处理持续时间变化 - 改为使用选中课程的时长
  const handleDurationChange = (selectedVideo) => {
    if (!selectedVideo || !selectedVideo.duration) {
      setCalculatedEndTime(null);
      return;
    }
    
    // 如果表单中已有开始时间，则更新表单，保持一致性
    const startTime = streamForm.getFieldValue('startTime');
    if (startTime) {
      // 使用视频时长（秒）直接添加到开始时间
      const durationSeconds = selectedVideo.duration;
      
      // 直接使用原始的moment对象，不做任何转换
      const endTime = startTime.clone().add(durationSeconds, 'seconds');
      
      // 更新计算好的结束时间state
      setCalculatedEndTime(endTime);
      
      // 同时更新表单中的隐藏字段，用于提交数据
      streamForm.setFieldsValue({ 
        startTime: startTime, // 确保表单中的开始时间秒数为0
        calculatedEndTime: endTime 
      });
    }
  };
  
  // 重置推流表单
  const resetStreamForm = () => {
    streamForm.resetFields();
    setSelectedVideo(null);
    setSelectedGroups([]);
    setCalculatedEndTime(null);
    setCanSubmit(false);
    setFilterExpanded(false); // 重置筛选框展开状态
  };
  
  // 关闭对话框
  const closeStreamModal = () => {
    setStreamModalVisible(false);
    setSelectedVideo(null);
    resetStreamForm();
    setCalculatedEndTime(null); // 重置计算好的结束时间
    setCanSubmit(false); // 重置提交状态
    setSelectedGroups([]); // 重置班级选择
    setCurrentSearchKey(''); // 重置搜索关键词
    setGroupPagination({ current: 0, hasMore: true, total: 0 }); // 重置分页状态
    setFilterExpanded(false); // 重置筛选框展开状态
  };
  
  // 显示新建直播对话框
  const showCreateStreamModal = async () => {
    // 打开对话框前获取视频列表
    resetStreamForm();
    setSelectedVideo(null);
    setCalculatedEndTime(null); // 重置计算好的结束时间
    setCanSubmit(false); // 初始化为不可提交
    setSelectedGroups([]); // 重置班级选择
    setCurrentSearchKey(''); // 重置搜索关键词
    setGroupPagination({ current: 0, hasMore: true, total: 0 }); // 重置分页状态
    setStreamModalVisible(true);
    // 重置筛选表单和分页
    streamForm.resetFields();
    setVideoPage(1);
    setVideoPageSize(10);
    // 重置筛选条件并获取视频文件
    setFilters({
      grade_name: undefined,
      class_name: undefined,
      course_name: undefined,
      teacher_name: '',
      searchText: '',
      custom_tag: undefined  // 新增：自定义标签筛选
    });
    // 获取视频文件列表
    await fetchVideoFiles();
  };

  // 检查表单有效性
  const checkFormValidity = () => {
    const videoId = streamForm.getFieldValue('videoId');
    const title = streamForm.getFieldValue('title');
    const teacherRealName = streamForm.getFieldValue('teacher_real_name');
    const startTime = streamForm.getFieldValue('startTime');
    
    const isValid = videoId && 
                   title && title.trim() !== '' &&
                   teacherRealName && teacherRealName.trim() !== '' &&
                   startTime; // 开始时间也是必填的
    setCanSubmit(isValid);
  };

  // 处理视频选择变化
  const handleVideoChange = (videoId) => {
    const video = videoFiles.find(v => v.id === videoId);
    setSelectedVideo(video);
    
    // 自动填充表单字段
    if (video) {
      const formValues = {
        videoId: videoId
      };
      
      // 自动填充课堂名称（优先使用课堂名称，即video.name）
      if (video.name) {
        formValues.title = video.name;
      }
      
      // 自动填充老师称呼
      if (video.teacher_name) {
        formValues.teacher_real_name = video.teacher_name;
      }

      // 设置表单值
      streamForm.setFieldsValue(formValues);
    
    // 如果选择了视频且启用了定时推流，重新计算结束时间
      handleDurationChange(video);
      
      // 在设置表单值后检查表单有效性
      setTimeout(() => {
        checkFormValidity();
      }, 100); // 增加延迟确保表单值已更新
    } else {
      // 如果没有选择视频，检查表单有效性
    setTimeout(checkFormValidity, 0);
    }
  };

  // 计算结束时间
  const calculateEndTime = (startTime, selectedVideo) => {
    if (!startTime || !selectedVideo || !selectedVideo.duration) return null;
    
    // 使用视频时长（秒）直接添加到开始时间
    const durationSeconds = selectedVideo.duration;
    
    // 直接clone并添加秒数
    const endMoment = startTime.clone().add(durationSeconds, 'seconds');
    return endMoment;
  };

  // 处理推流提交
  const handleStreamSubmit = async () => {
    setSubmittingTask(true);
    try {
      // 先获取当前表单的所有值进行调试
      const currentFormValues = streamForm.getFieldsValue();
      
      const values = await streamForm.validateFields();
      
      if (!values.videoId) {
        messageApi.error('请选择视频');
        return;
      }
      
      // 构建班级名称数组
      let groupNameArray = [];
      if (values.groupIds && values.groupIds.length > 0) {
        groupNameArray = values.groupIds.map(groupId => {
          const foundGroup = groupOptions.find(option => option.value === groupId);
          return foundGroup ? foundGroup.groupName : `未知班级(${groupId})`;
        });
      }
      
      // 从选择的视频文件中获取teacher_id作为speaker_id
      let speaker_id = null;
      if (selectedVideo && selectedVideo.teacher_id) {
        speaker_id = selectedVideo.teacher_id;
      } else {
        console.warn('选择的视频中没有teacher_id字段:', selectedVideo);
      }
      
      // 构建请求数据
      const streamData = {
        videoFile: values.videoId,
        title: values.title,
        teacher_real_name: values.teacher_real_name,
        teacher_desc: values.teacher_desc || '',
        description: values.description || '',
        group_ids: values.groupIds || [],
        groupName: groupNameArray,
        speaker_id: speaker_id  // 使用从视频文件中获取的teacher_id
      };
      
      // 添加课程持续时间信息
      if (selectedVideo && selectedVideo.duration) {
        streamData.duration = selectedVideo.duration;
      } else {
        console.warn('未找到视频时长信息，selectedVideo:', selectedVideo);
        // 尝试从videoFiles中重新获取
        const video = videoFiles.find(v => v.id === values.videoId);
        if (video && video.duration) {
          streamData.duration = video.duration;
        } else {
          console.error('无法获取视频时长，video:', video);
        }
      }
      
      // 如果启用了定时推流，添加开始和结束时间
      if (values.startTime) {
        // 使用本地时间格式，不转换为UTC
        streamData.startTime = values.startTime.format('YYYY-MM-DD HH:mm:ss');
        
        // 获取结束时间 - 从表单中直接获取计算好的结束时间
        const calculatedEndTime = streamForm.getFieldValue('calculatedEndTime');
        if (calculatedEndTime) {
          streamData.endTime = calculatedEndTime.format('YYYY-MM-DD HH:mm:ss');
        } else if (selectedVideo && selectedVideo.duration) {
          // 如果没有预先计算的结束时间，使用选中视频的时长重新计算
          const durationSeconds = selectedVideo.duration;
          const endTime = values.startTime.clone().add(durationSeconds, 'seconds');
          streamData.endTime = endTime.format('YYYY-MM-DD HH:mm:ss');
        }
      }
      
      // 开始推流
      const result = await startStream(streamData);
      
      messageApi.success('已创建定时推流任务');
      setStreamModalVisible(false);
      
      // 强制立即刷新活跃直播流数据
      setTimeout(() => {
        fetchActiveStreams(1, streamPageSize); // 创建新直播后回到第一页
      }, 1000);
    } catch (error) {
      console.error('提交表单失败:', error);
      
      // 如果是表单验证错误，显示更友好的提示
      if (error.errorFields && error.errorFields.length > 0) {
        const firstError = error.errorFields[0];
        if (firstError.name[0] === 'videoId') {
          messageApi.error('请选择要直播的课程');
        } else {
          messageApi.error('请检查表单填写是否完整');
        }
      } else {
        // 如果是API调用错误
      messageApi.error('开始推流失败: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setSubmittingTask(false);
    }
  };

  // 处理查看直播详情
  const showStreamDetails = (stream) => {
    setSelectedStream(stream);
    setDetailDrawerVisible(true);
  };

  // 复制文本到剪贴板
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        messageApi.success('已复制到剪贴板');
      },
      () => {
        messageApi.error('复制失败');
      }
    );
  };

  // 处理清理临时文件
  const handleCleanupTempFiles = async () => {
    try {
      // 显示加载提示
      const hide = messageApi.loading('正在清理临时文件...', 0);
      
      // 调用API
      const result = await cleanupTempFiles();
      
      // 关闭加载提示
      hide();
      
      // 显示成功消息
      if (result && result.data) {
        const details = result.data.details || {};
        messageApi.success(
          `临时文件清理完成! 共清理 ${details.deleted_files || 0} 个孤立文件，处理 ${details.db_temp_files || 0} 个数据库记录`
        );
      } else {
        messageApi.success('临时文件清理完成!');
      }
      
      // 刷新活跃直播流数据
      fetchActiveStreams(streamPage, streamPageSize); // 保持当前分页状态
    } catch (error) {
      console.error('清理临时文件失败:', error);
      messageApi.error('清理临时文件失败: ' + (error.message || '未知错误'));
    }
  };

  // 处理开启直播外链
  const handleEnableExternalLink = async (recordId) => {
    try {
      const hide = messageApi.loading('正在开启直播外链...', 0);
      
      const result = await enableExternalLink(recordId);
      
      hide();
      
      if (result && result.code === 0) {
        messageApi.success('直播外链开启成功！');
        
        // 刷新直播流数据
        fetchActiveStreams(streamPage, streamPageSize);
        
        // 如果当前正在查看详情，更新详情数据
        if (selectedStream && selectedStream.id === recordId) {
          const updatedStream = { 
            ...selectedStream, 
            ...result.data,
            external_link_enabled: true
          };
          setSelectedStream(updatedStream);
        }
      } else {
        messageApi.error('开启直播外链失败: ' + (result?.message || '未知错误'));
      }
    } catch (error) {
      console.error('开启直播外链失败:', error);
      messageApi.error('开启直播外链失败: ' + (error.message || '未知错误'));
    }
  };

  // 处理关闭直播外链
  const handleCloseExternalLink = async (recordId) => {
    try {
      const hide = messageApi.loading('正在关闭直播外链...', 0);
      
      const result = await closeExternalLink(recordId);
      
      hide();
      
      if (result && result.code === 0) {
        messageApi.success('直播外链关闭成功！');
        
        // 刷新直播流数据
        fetchActiveStreams(streamPage, streamPageSize);
        
        // 如果当前正在查看详情，更新详情数据
        if (selectedStream && selectedStream.id === recordId) {
          const updatedStream = { 
            ...selectedStream, 
            external_link_enabled: false
          };
          setSelectedStream(updatedStream);
        }
      } else {
        messageApi.error('关闭直播外链失败: ' + (result?.message || '未知错误'));
      }
    } catch (error) {
      console.error('关闭直播外链失败:', error);
      messageApi.error('关闭直播外链失败: ' + (error.message || '未知错误'));
    }
  };

  // 处理获取潜在学生
  const handleFetchVisitorInfo = async (streamId, meetingId) => {
    console.log('=== handleFetchVisitorInfo 调试日志 ===');
    console.log('输入参数:', { streamId, meetingId });
    console.log('参数类型:', { 
      streamIdType: typeof streamId, 
      meetingIdType: typeof meetingId 
    });
    
    if (!streamId || !meetingId) {
      console.error('参数验证失败:', { streamId, meetingId });
      messageApi.error('参数不完整：缺少直播任务ID或课堂ID');
      return;
    }

    setFetchingVisitorInfo(true);
    try {
      const hide = messageApi.loading('正在获取潜在学生...', 0);
      
      console.log('准备调用fetchVisitorInfo...');
      const result = await fetchVisitorInfo(streamId, meetingId);
      console.log('fetchVisitorInfo返回结果:', result);
      
      hide();
      
      if (result && (result.code === 0 || result.success === true)) {
        // 兼容两种响应格式
        const data = result.data || result;
        const totalFetched = data.total_fetched || 0;
        const totalSaved = data.total_saved || 0;
        
        messageApi.success(`潜在学生获取成功！获取${totalFetched}条，保存${totalSaved}条`);
        
        // 获取成功后立即刷新潜在学生列表
        await loadVisitorInfo(streamId);
      } else {
        console.error('API返回错误:', result);
        messageApi.error('获取潜在学生失败: ' + (result?.message || '未知错误'));
      }
    } catch (error) {
      console.error('=== handleFetchVisitorInfo 错误日志 ===');
      console.error('捕获到错误:', error);
      console.error('错误消息:', error.message);
      console.error('错误堆栈:', error.stack);
      messageApi.error('获取潜在学生失败: ' + (error.message || '未知错误'));
    } finally {
      setFetchingVisitorInfo(false);
    }
  };

  // 加载潜在学生
  const loadVisitorInfo = async (streamId) => {
    console.log('=== loadVisitorInfo 调试日志 ===');
    console.log('输入参数 streamId:', streamId);
    
    setVisitorLoading(true);
    try {
      console.log('开始调用 getVisitorInfo...');
      const result = await getVisitorInfo(streamId);
      console.log('getVisitorInfo 返回结果:', result);
      console.log('result 类型:', typeof result);
      console.log('result.code:', result?.code);
      console.log('result.data:', result?.data);
      
      // 处理不同的响应格式
      let visitorData = [];
      
      if (result && result.code === 0) {
        // 标准格式：{code: 0, message: 'success', data: [...]}
        if (Array.isArray(result.data)) {
          // 直接是数组
          visitorData = result.data;
        } else if (result.data && Array.isArray(result.data.items)) {
          // 分页格式：{code: 0, data: {items: [...], total: 26}}
          visitorData = result.data.items;
        } else {
          console.warn('无法识别的data格式:', result.data);
          visitorData = [];
        }
      } else if (Array.isArray(result)) {
        // 直接返回数组格式（可能是axios拦截器处理前的原始响应）
        console.log('收到直接数组格式的响应');
        visitorData = result;
      } else {
        console.error('API返回非成功状态:', result);
        console.error('错误消息:', result?.message);
        visitorData = [];
      }
      
      console.log('最终设置的潜在学生数据:', visitorData);
      console.log('潜在学生数量:', Array.isArray(visitorData) ? visitorData.length : '不是数组');
      setVisitorInfo(visitorData);
      
    } catch (error) {
      console.error('=== loadVisitorInfo 错误详情 ===');
      console.error('错误对象:', error);
      console.error('错误类型:', typeof error);
      console.error('错误消息:', error?.message);
      console.error('错误堆栈:', error?.stack);
      console.error('错误响应:', error?.response);
      console.error('错误响应数据:', error?.response?.data);
      console.error('获取潜在学生失败:', error?.message || error || '未知错误');
      setVisitorInfo([]);
    } finally {
      setVisitorLoading(false);
    }
  };

  // 当选择的直播流改变时，加载潜在学生
  useEffect(() => {
    if (selectedStream && selectedStream.id) {
      loadVisitorInfo(selectedStream.id);
    } else {
      setVisitorInfo([]);
    }
  }, [selectedStream?.id]);

  // 课程时长归整处理
  const formatRoundedDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '30分钟';
    
    const minutes = Math.ceil(seconds / 60);
    
    // 按30分钟归整
    if (minutes <= 30) {
      return '30分钟';
    } else if (minutes <= 60) {
      return '1小时';
    } else if (minutes <= 90) {
      return '1小时30分钟';
    } else if (minutes <= 120) {
      return '2小时';
    } else if (minutes <= 150) {
      return '2小时30分钟';
    } else if (minutes <= 180) {
      return '3小时';
    } else {
      // 超过3小时的按小时归整
      const hours = Math.ceil(minutes / 60);
      return `${hours}小时`;
    }
  };

  // 生成二维码
  const generateQRCode = async (url) => {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrCodeDataUrl);
      setQrCodeModalVisible(true);
    } catch (error) {
      console.error('生成二维码失败:', error);
      messageApi.error('生成二维码失败');
    }
  };

  // 保存海报
  const saveQRCodePoster = () => {
    try {
      // 创建一个临时的canvas来绘制海报
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // 设置canvas尺寸，增加高度以容纳更多内容
      canvas.width = 500;
      canvas.height = 800;
      
      // 绘制墨绿色渐变背景
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#1f4e3d');
      gradient.addColorStop(1, '#0f2419');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // 绘制状态标签
      if (selectedStream.status) {
        ctx.fillStyle = selectedStream.status === '推流中' ? '#52c41a' : 
                       selectedStream.status === '等待开始' ? '#faad14' : '#d9d9d9';
        ctx.fillRect(canvas.width - 100, 20, 80, 30);
        ctx.fillStyle = 'white';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(selectedStream.status, canvas.width - 60, 38);
      }
      
      // 绘制品牌标题
      ctx.fillStyle = 'white';
      ctx.font = 'bold 40px Microsoft YaHei, Arial';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 3;
      ctx.fillText('同方专转本', canvas.width / 2, 80);
      
      // 重置阴影
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      
      // 绘制副标题
      ctx.font = '22px Arial';
      ctx.fillText('公开课', canvas.width / 2, 115);
      
      // 绘制课程标题
      ctx.font = 'bold 20px Arial';
      ctx.fillText(selectedStream.title || selectedStream.videoFile, canvas.width / 2, 150);
      
      // 绘制讲师信息（带背景）
      if (selectedStream.teacher_real_name) {
        // 绘制讲师信息背景
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        const teacherText = `讲师：${selectedStream.teacher_real_name}`;
        const textWidth = ctx.measureText(teacherText).width;
        ctx.fillRect((canvas.width - textWidth - 24) / 2, 165, textWidth + 24, 30);
        
        // 绘制讲师文字
        ctx.fillStyle = 'white';
        ctx.font = '16px Arial';
        ctx.fillText(teacherText, canvas.width / 2, 185);
      }
      
      // 绘制二维码白色背景区域
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.fillRect(50, 220, canvas.width - 100, 280);
      
      // 绘制二维码
      if (qrCodeUrl) {
        const img = new window.Image();
        img.onload = () => {
          // 绘制二维码白色内边框
          ctx.fillStyle = 'white';
          ctx.fillRect((canvas.width - 220) / 2, 240, 220, 220);
          
          // 绘制二维码
          ctx.drawImage(img, (canvas.width - 200) / 2, 250, 200, 200);
          
          // 绘制二维码下方的提示文字
          ctx.fillStyle = '#1f4e3d';
          ctx.font = 'bold 18px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('扫描二维码观看直播', canvas.width / 2, 485);
          
          // 绘制底部信息区域背景
          ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
          ctx.fillRect(0, 520, canvas.width, 120);
          
          // 绘制开始时间信息卡片
          if (selectedStream.startTime) {
            // 开始时间卡片背景
            ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
            ctx.fillRect(80, 540, 160, 60);
            
            // 开始时间标签
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.font = '12px Arial';
            ctx.fillText('开始时间', 160, 555);
            
            // 开始时间值
            ctx.fillStyle = 'white';
            ctx.font = '14px Arial';
            const startTimeText = new Date(selectedStream.startTime).toLocaleString();
            ctx.fillText(startTimeText, 160, 580);
          }
          
          // 绘制课程时长信息卡片
          if (selectedStream.duration) {
            // 课程时长卡片背景
            ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
            ctx.fillRect(260, 540, 160, 60);
            
            // 课程时长标签
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.font = '12px Arial';
            ctx.fillText('课程时长', 340, 555);
            
            // 课程时长值
            ctx.fillStyle = 'white';
            ctx.font = 'bold 16px Arial';
            ctx.fillText(formatRoundedDuration(selectedStream.duration), 340, 580);
          }
          
          // 绘制底部装饰条
          const decorGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
          decorGradient.addColorStop(0, '#1f4e3d');
          decorGradient.addColorStop(0.25, '#2d5a47');
          decorGradient.addColorStop(0.5, '#3b6651');
          decorGradient.addColorStop(0.75, '#49725b');
          decorGradient.addColorStop(1, '#577e65');
          ctx.fillStyle = decorGradient;
          ctx.fillRect(0, canvas.height - 6, canvas.width, 6);
          
          // 下载图片
          const link = document.createElement('a');
          link.download = `${selectedStream.title || selectedStream.videoFile}_直播海报.png`;
          link.href = canvas.toDataURL();
          link.click();
          
          messageApi.success('海报已保存');
        };
        img.src = qrCodeUrl;
      }
    } catch (error) {
      console.error('保存海报失败:', error);
      messageApi.error('保存海报失败');
    }
  };

  // 取消任务（将定时任务取消）
  const handleCancelTask = async (recordId) => {
    try {
      // 调用API更新任务状态为已取消（状态码4）
      const response = await axiosInstance.put(`/live-sim/streams/${recordId}/status?status=4`);
      
      if (response && response.data && response.data.code === 0) {
        messageApi.success('已取消定时任务');
      } else {
        messageApi.success('已处理取消任务请求');
      }
      
      // 刷新直播流数据
      fetchActiveStreams(streamPage, streamPageSize); // 保持当前分页状态
    } catch (error) {
      console.error('取消任务失败:', error);
      
      // 检查是否是"记录不存在"的错误
      if (error.message && error.message.includes('记录不存在')) {
        // 记录不存在可能意味着任务已经被其他进程取消了，这种情况下显示成功消息
        messageApi.success('任务已取消（可能已被其他操作处理）');
        // 刷新列表以获取最新状态
        fetchActiveStreams(streamPage, streamPageSize);
      } else {
        // 其他错误才显示失败消息
        messageApi.error('取消任务失败: ' + (error.message || '未知错误'));
        
        // 尝试刷新列表
        setTimeout(() => {
          fetchActiveStreams(streamPage, streamPageSize); // 保持当前分页状态
        }, 2000);
      }
    }
  };

  // 重新开始直播
  const handleRestartStream = (record) => {
    // 设置选择的视频为该记录的视频
    const videoId = record.videoId;
    
    // 预填充表单
    streamForm.setFieldsValue({
      videoId: videoId
    });
    
    // 找到对应的视频信息
    const video = videoFiles.find(v => v.id === videoId);
    if (video) {
      setSelectedVideo(video);
    } else {
      // 如果在当前列表中找不到，直接使用记录中的信息
      setSelectedVideo({
        id: videoId,
        name: record.videoFile,
        teacher_name: record.teacherName,
        course_name: record.courseName
      });
    }
    
    // 打开创建直播对话框
    setStreamModalVisible(true);
    
    // 显示一个通知，提示用户正在重新开始直播
    messageApi.info('您可以修改推流设置并点击确定重新开始直播');
  };

  // 处理停止推流的函数需要修改以更新状态描述
  const handleStopStream = async (videoId, recordId) => {
    try {
      const result = await stopStream(videoId);
      
      if (result && result.code === 0) {
        messageApi.success(result.data?.message || '已停止推流');
        
        // 更新状态为手动终止（状态码5）
        if (recordId) {
          try {
            const updateResponse = await axiosInstance.put(`/live-sim/streams/${recordId}/status?status=5`);
          } catch (updateError) {
            console.error('更新状态失败:', updateError);
          }
        }
      } else {
        messageApi.success('已处理停止推流请求');
      }
      
      // 刷新活跃直播流数据
      fetchActiveStreams(streamPage, streamPageSize); // 保持当前分页状态
    } catch (error) {
      console.error('停止推流失败:', error);
      // 显示详细的错误信息
      let errorMsg = '停止推流失败';
      if (error.message) {
        errorMsg += ': ' + error.message;
      }
      messageApi.error(errorMsg);
      
      // 即使出错也尝试刷新列表
      setTimeout(() => {
        fetchActiveStreams(streamPage, streamPageSize); // 保持当前分页状态
      }, 2000);
    }
  };

  // 活跃直播表格列
  const streamColumns = [
    {
      title: '课程信息',
      key: 'courseInfo',
      render: (_, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            {/* 公开课/内部课图标 */}
            {record.external_link_enabled ? (
              <Tag 
                color="green" 
                style={{ 
                  marginRight: '8px', 
                  fontSize: '10px',
                  padding: '2px 6px',
                  lineHeight: '1.2',
                  borderRadius: '4px'
                }}
              >
                公开课
              </Tag>
            ) : (
              <Tag 
                color="blue" 
                style={{ 
                  marginRight: '8px', 
                  fontSize: '10px',
                  padding: '2px 6px',
                  lineHeight: '1.2',
                  borderRadius: '4px'
                }}
              >
                内部课
              </Tag>
            )}
            <Text strong>{record.videoFile}</Text>
          </div>
          {record.teacherName && <div><Text type="secondary">讲师: {record.teacherName}</Text></div>}
          {record.courseName && <div><Text type="secondary">课程: {record.courseName}</Text></div>}
          {record.scheduled && <Tag color="blue" icon={<ClockCircleOutlined />}>定时任务</Tag>}
          {record.status && 
            <Tag color={
              record.status === '推流中' ? 'green' : 
              record.status === '等待开始' ? 'orange' : 
              record.status === '已结束' ? 'gray' : 
              record.status === '异常终止' ? 'red' : 
              record.status === '已取消' ? 'purple' :
              record.status === '手动终止' ? 'blue' :
              'default'
            }>
              {record.status}
            </Tag>
          }
        </div>
      )
    },
    {
      title: '推流密钥',
      dataIndex: 'streamKey',
      key: 'streamKey',
      render: (text) => (
        <Text
          style={{ width: 150 }}
          ellipsis={{ 
            tooltip: text 
          }}
        >
          {text}
        </Text>
      )
    },
    {
      title: '开始时间',
      key: 'startTime',
      dataIndex: 'startTime',
      defaultSortOrder: 'descend',
      sorter: (a, b) => {
        // 按计划开始时间排序，如果没有计划时间则排在最后
        const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
        const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
        return timeA - timeB;
      },
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>计划:</Text>
            <br />
            <Text style={{ fontSize: '13px' }}>
              {record.startTime ? new Date(record.startTime).toLocaleString() : '-'}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>实际:</Text>
            <br />
            <Text style={{ fontSize: '13px' }}>
              {record.actualStartTime ? new Date(record.actualStartTime).toLocaleString() : '-'}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: '结束时间',
      key: 'endTime',
      dataIndex: 'endTime',
      sorter: (a, b) => {
        // 按计划结束时间排序，如果没有计划时间则排在最后
        const timeA = a.endTime ? new Date(a.endTime).getTime() : 0;
        const timeB = b.endTime ? new Date(b.endTime).getTime() : 0;
        return timeA - timeB;
      },
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>计划:</Text>
            <br />
            <Text style={{ fontSize: '13px' }}>
              {record.endTime ? new Date(record.endTime).toLocaleString() : '手动结束'}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>实际:</Text>
            <br />
            <Text style={{ fontSize: '13px' }}>
              {record.actualEndTime ? new Date(record.actualEndTime).toLocaleString() : '-'}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: '时长',
      key: 'duration',
      dataIndex: 'duration',
      sorter: (a, b) => {
        // 按视频时长排序，如果没有时长则排在最后
        const durationA = a.duration || 0;
        const durationB = b.duration || 0;
        return durationA - durationB;
      },
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>视频:</Text>
            <br />
            <Text style={{ fontSize: '13px' }}>
              {formatDuration(record.duration)}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>直播:</Text>
            <br />
            <Text style={{ fontSize: '13px' }}>
              {formatDuration(record.actualDuration)}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EyeOutlined />}
            onClick={() => showStreamDetails(record)}
          >
            详情
          </Button>
          
          {/* 等待开始的任务显示取消任务按钮 */}
          {record.status === '等待开始' && (
            <Popconfirm
              title="确定要取消这个定时任务吗?"
              onConfirm={() => handleCancelTask(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="primary" danger icon={<StopOutlined />}>
                取消任务
              </Button>
            </Popconfirm>
          )}
          
          {/* 推流中的任务显示停止推流和重新开始按钮 */}
          {record.status === '推流中' && (
            <>
              <Popconfirm
                title="确定要停止推流吗?"
                onConfirm={() => handleStopStream(record.videoId, record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="primary" danger icon={<StopOutlined />}>
                  停止推流
                </Button>
              </Popconfirm>
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />}
                onClick={() => handleRestartStream(record)}
              >
                重新开始
              </Button>
            </>
          )}
          
          {/* 已结束或异常终止的任务显示重新开始按钮 */}
          {(record.status === '已结束' || record.status === '异常终止' || record.status === '手动终止') && (
            <Button 
              type="primary" 
              icon={<PlayCircleOutlined />}
              onClick={() => handleRestartStream(record)}
            >
              重新开始
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={4} style={{ margin: 0 }}>所有直播任务 ({activeStreams.length})</Title>
            <Space>
              <Button
                icon={<SyncOutlined />}
                onClick={handleManualRefresh}
              >
                刷新
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={showCreateStreamModal}
              >
                新建直播
              </Button>
              <Tooltip title="清理所有已结束直播的临时视频文件，释放磁盘空间">
                <Button 
                  icon={<DeleteOutlined />}
                  onClick={handleCleanupTempFiles}
                >
                  清理临时文件
                </Button>
              </Tooltip>
            </Space>
          </div>
        }
      >
        <Table
          columns={streamColumns}
          dataSource={activeStreams}
          rowKey="id"
          pagination={{
            current: streamPage,
            pageSize: streamPageSize,
            total: streamTotal,
            onChange: handleStreamPageChange,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          loading={loading}
          sortDirections={['descend', 'ascend']}
          onChange={(pagination, filters, sorter) => {
            // 可以在这里处理排序逻辑，目前使用前端排序
          }}
          locale={{ 
            emptyText: (
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    当前没有任何直播任务记录
                    <br />
                    点击"新建直播"按钮创建一个新的直播任务
                  </span>
                }
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={showCreateStreamModal}>
                  新建直播
                </Button>
              </Empty>
            )
          }}
        />
      </Card>

      {/* 新建直播表单 */}
      <Modal
        title="新建直播"
        open={streamModalVisible}
        onCancel={closeStreamModal}
        width={1200}
        footer={[
          <Button key="cancel" onClick={closeStreamModal}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            disabled={!canSubmit}
            loading={submittingTask}
            onClick={handleStreamSubmit}
          >
            {submittingTask ? '创建中...' : '创建定时任务'}
          </Button>
        ]}
      >
        <Spin spinning={loadingVideos} tip="加载课程列表中...">
          {/* 主表单 */}
          <Form
            form={streamForm}
            layout="vertical"
          >
            {/* 左右两栏布局 */}
            <Row gutter={24}>
              {/* 左侧表单区域 - 30% */}
              <Col span={7}>
                <Form.Item
                  name="title"
                  label="课堂名称"
                  rules={[{ required: true, message: '请输入课堂名称' }]}
                  tooltip="课堂名称将显示在伯索平台上"
                >
                  <Input 
                    placeholder="请输入课堂名称" 
                    onChange={() => {
                      setTimeout(checkFormValidity, 0);
                    }}
                  />
                </Form.Item>
                
                <Form.Item
                  name="description"
                  label="课堂简介"
                  tooltip="课堂的简介信息（可选）"
                >
                  <Input.TextArea 
                    placeholder="请输入课堂简介（可选）" 
                    rows={3}
                    maxLength={500}
                    showCount
                  />
                </Form.Item>
                
                <Form.Item
                  name="teacher_real_name"
                  label="老师称呼"
                  rules={[{ required: true, message: '请输入老师称呼' }]}
                  tooltip="老师在课堂中的显示名称"
                >
                  <Input 
                    placeholder="请输入老师称呼" 
                    onChange={() => {
                      setTimeout(checkFormValidity, 0);
                    }}
                  />
                </Form.Item>
                
                <Form.Item
                  name="teacher_desc"
                  label="老师简介"
                  tooltip="老师的简介信息（可选）"
                >
                  <Input.TextArea 
                    placeholder="请输入老师简介（可选）" 
                    rows={3}
                    maxLength={500}
                    showCount
                  />
                </Form.Item>
                
                <Form.Item
                  name="startTime"
                  label="推流开始时间"
                  rules={[{ required: true, message: '请选择推流开始时间' }]}
                  tooltip="选择推流的开始时间，系统将自动在指定时间开始推流"
                >
                  <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    format="YYYY-MM-DD HH:mm"
                    placeholder="开始时间"
                    disabledDate={(current) => current && current < moment().startOf('day')}
                    disabledTime={(current) => {
                      // 如果是今天，禁用早于当前时间的时间
                      if (current && current.isSame(moment(), 'day')) {
                        const now = moment();
                        return {
                          disabledHours: () => {
                            const hours = [];
                            for (let i = 0; i < now.hour(); i++) {
                              hours.push(i);
                            }
                            return hours;
                          },
                          disabledMinutes: (selectedHour) => {
                            if (selectedHour === now.hour()) {
                              const minutes = [];
                              for (let i = 0; i <= now.minute(); i++) {
                                minutes.push(i);
                              }
                              return minutes;
                            }
                            return [];
                          }
                        };
                      }
                      return {};
                    }}
                    style={{ width: '100%' }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    onChange={(date) => {
                      if (date) {
                        // 确保秒数为0
                        const startTime = date.clone().second(0);
                        
                        // 检查是否选择了视频
                        if (selectedVideo && selectedVideo.duration) {
                          // 使用选中视频的时长计算结束时间
                          const durationSeconds = selectedVideo.duration;
                          const endTime = startTime.clone().add(durationSeconds, 'seconds');
                          
                          // 更新计算好的结束时间state
                          setCalculatedEndTime(endTime);
                          
                          // 同时更新表单中的隐藏字段，用于提交数据
                          streamForm.setFieldsValue({
                            startTime: startTime, // 设置为秒数为0的时间
                            calculatedEndTime: endTime // 直接设置moment对象
                          });
                        } else {
                          // 如果没有选择视频，清空结束时间
                          setCalculatedEndTime(null);
                          streamForm.setFieldsValue({
                            startTime: startTime,
                            calculatedEndTime: null
                          });
                          console.warn('请先选择视频，才能计算结束时间');
                        }
                      } else {
                        // 如果清空了开始时间，也清空结束时间
                        setCalculatedEndTime(null);
                        streamForm.setFieldsValue({
                          calculatedEndTime: null
                        });
                      }
                      
                      // 检查表单有效性
                      setTimeout(checkFormValidity, 0);
                    }}
                  />
                </Form.Item>
                
                <Form.Item
                  name="groupIds"
                  label="选择班级"
                  tooltip="选择要推流到的伯索班级，学生将在该班级中观看直播（可多选）"
                >
                  <Select
                    mode="multiple"
                    placeholder="请选择班级（可选，支持多选）"
                    loading={groupLoading}
                    showSearch
                    allowClear
                    filterOption={false}
                    onSearch={(value) => {
                      // 防抖搜索
                      clearTimeout(window.groupSearchTimeout);
                      window.groupSearchTimeout = setTimeout(() => {
                        setCurrentSearchKey(value);
                        fetchGroupOptions(value);
                      }, 300);
                    }}
                    onFocus={() => {
                      // 聚焦时如果没有数据则获取
                      if (groupOptions.length === 0) {
                        fetchGroupOptions();
                      }
                    }}
                    onChange={(value) => {
                      setSelectedGroups(value);
                    }}
                    onPopupScroll={(e) => {
                      const { target } = e;
                      // 当滚动到底部且还有更多数据时，加载更多
                      if (target.scrollTop + target.offsetHeight === target.scrollHeight && 
                          groupPagination.hasMore && 
                          !groupLoading) {
                        fetchGroupOptions(currentSearchKey, true);
                      }
                    }}
                    style={{ width: '100%' }}
                    dropdownRender={(menu) => (
                      <div>
                        {menu}
                        {groupPagination.hasMore && (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '8px',
                            borderTop: '1px solid #f0f0f0'
                          }}>
                            <Button 
                              type="link" 
                              size="small"
                              loading={groupLoading}
                              onClick={() => fetchGroupOptions(currentSearchKey, true)}
                            >
                              加载更多
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  >
                    {groupOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%'
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ 
                              fontSize: '14px',
                              color: '#333'
                            }}>
                              {option.groupName}
                            </span>
                          </div>
                          <div style={{ 
                            flexShrink: 0,
                            textAlign: 'right',
                            fontSize: '12px', 
                            color: '#666'
                          }}>
                            学生数: {option.sCount}
                          </div>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                
                {/* 隐藏的计算结束时间字段，用于后端提交 */}
                <Form.Item name="calculatedEndTime" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
              </Col>
              
              {/* 右侧课程选择区域 - 70% */}
              <Col span={17}>
                {/* 课程选择区域 - 在主表单内部 */}
                <Form.Item
                  name="videoId"
                  label="选择视频"
                  rules={[{ required: true, message: '请选择要直播的视频' }]}
                >
          <Card 
            size="small" 
            style={{ marginBottom: 16 }}
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>视频筛选</span>
              <Space>
                <Button 
                  icon={<SearchOutlined />} 
                  onClick={handleApplyFilters}
                  type="primary"
                  size="small"
                >
                  搜索
                </Button>
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={handleResetFilters}
                  size="small"
                >
                  重置
                </Button>
                          <Button 
                            type="text"
                            size="small"
                            onClick={() => setFilterExpanded(!filterExpanded)}
                            icon={filterExpanded ? <UpOutlined /> : <DownOutlined />}
                          >
                            {filterExpanded ? '收起筛选' : '展开筛选'}
                          </Button>
              </Space>
                      </div>
                    }
                  >
                    {filterExpanded && (
                      <div style={{ marginBottom: '16px' }}>
                        <Row gutter={[8, 8]}>
                          <Col span={6}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择届次"
                  allowClear
                  options={gradeOptions}
                              value={filters.grade_name}
                              onChange={(value) => handleFilterChange({ grade_name: value })}
                />
                          </Col>
                          <Col span={6}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择班次"
                  allowClear
                  options={classOptions}
                              value={filters.class_name}
                              onChange={(value) => handleFilterChange({ class_name: value })}
                />
                          </Col>
                          <Col span={6}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择课程"
                  allowClear
                  options={courseOptions}
                              value={filters.course_name}
                              onChange={(value) => handleFilterChange({ course_name: value })}
                />
                          </Col>
                          <Col span={6}>
                <Select 
                  style={{ width: '100%' }} 
                  placeholder="选择标签"
                  allowClear
                  loading={tagLoading}
                  options={tagOptions}
                              value={filters.custom_tag}
                              onChange={(value) => handleFilterChange({ custom_tag: value })}
                />
                          </Col>
                          <Col span={24}>
                <Input 
                  placeholder="输入教师或课程名称" 
                  style={{ width: '100%' }}
                  allowClear
                              value={filters.searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                          </Col>
                        </Row>
                      </div>
                    )}
                    
                    <div style={{ height: '400px', overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: '2px', padding: '8px' }}>
                <Radio.Group 
                        onChange={(e) => {
                          const videoId = e.target.value;
                          handleVideoChange(videoId);
                          // 确保表单字段也被更新
                          streamForm.setFieldsValue({ videoId: videoId });
                          // 检查表单有效性
                          setTimeout(checkFormValidity, 150);
                        }} 
                  style={{ width: '100%', display: 'block' }}
                >
                  {loadingVideos ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin size="small" />
                      <div>加载课程中...</div>
                    </div>
                  ) : videoFiles.length === 0 ? (
                    <Empty description="没有匹配的课程，请调整筛选条件" />
                  ) : (
                    <div style={{ width: '100%' }}>
                      {videoFiles.map(video => (
                        <div key={video.id} style={{ 
                          width: '100%', 
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}>
                          <Radio 
                            value={video.id} 
                            disabled={video.streaming} 
                            style={{ 
                              marginTop: '12px',
                              marginRight: '8px',
                              flexShrink: 0
                            }} 
                          />
                          <div style={{ 
                            flex: 1,
                            padding: '12px', 
                            backgroundColor: '#fafafa',
                            borderRadius: '6px',
                            border: 'none',
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            minHeight: '80px'
                          }}>
                            <div style={{ 
                              flex: 1, 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minWidth: '0', 
                              paddingRight: '16px'
                            }}>
                              {/* 课程名称 */}
                              <div style={{ marginBottom: '8px' }}>
                                <Text style={{ 
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '14px'
                                }}>
                                  {video.name}
                                </Text>
                                {video.streaming && <Tag color="red" style={{ marginTop: '4px' }}>已在推流中</Tag>}
                              </div>
                              
                              {/* 届次、班次、课程、讲师信息在一行 - 不换行 */}
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#666', 
                                marginBottom: '4px', 
                                display: 'flex', 
                                gap: '12px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                              }}>
                                {video.grade_name && (
                                  <span style={{ flexShrink: 0 }}>届次: {video.grade_name}</span>
                                )}
                                {video.class_name && (
                                  <span style={{ flexShrink: 0 }}>班次: {video.class_name}</span>
                                )}
                                {video.course_name && (
                                  <span style={{ flexShrink: 0 }}>课程: {video.course_name}</span>
                                )}
                                {video.teacher_name && (
                                  <span style={{ 
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '120px'
                                  }}>讲师: {video.teacher_name}</span>
                                )}
                              </div>
                            </div>
                            
                            {/* 右侧信息：上课时间、标签、视频时长 */}
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'flex-end', 
                              gap: '4px', 
                                          width: '200px',
                              flexShrink: 0 
                            }}>
                              {/* 上课时间和标签在同一行 */}
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                flexWrap: 'wrap', 
                                justifyContent: 'flex-end',
                                width: '100%'
                              }}>
                                {/* 上课时间 */}
                                {video.course_start_time && (
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#52c41a', 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                    上课时间: {moment(video.course_start_time).format('YYYY-MM-DD HH:mm')}
                                  </div>
                                )}
                                
                                {/* 标签显示 */}
                                {video.custom_tags && video.custom_tags.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {video.custom_tags.map((tag, index) => (
                                      <Tag 
                                        key={index}
                                        size="small"
                                        style={{ 
                                          backgroundColor: '#f0f0f0', 
                                          color: '#666',
                                          border: '1px solid #d9d9d9',
                                          fontSize: '10px',
                                          margin: 0
                                        }}
                                      >
                                        {tag}
                                      </Tag>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* 视频时长 */}
                              {video.duration && video.duration > 0 && (
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px',
                                  whiteSpace: 'nowrap'
                                }}>
                                  <FieldTimeOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                                  <Text style={{ fontSize: '12px', color: '#1890ff' }}>
                                    {formatDuration(video.duration)}
                                  </Text>
                                  {video.duration < 600 && ( // 10分钟 = 600秒
                                    <Tag 
                                      size="small"
                                      style={{ 
                                        backgroundColor: '#ff4d4f', 
                                        color: '#fff',
                                        border: 'none',
                                        fontSize: '10px',
                                        margin: 0
                                      }}
                                    >
                                      测试课
                                    </Tag>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Radio.Group>
              </div>
            
            {/* 分页组件 */}
            {videoTotal > 0 && (
                      <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Pagination
                  current={videoPage}
                  pageSize={videoPageSize}
                  total={videoTotal}
                  onChange={handlePageChange}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total) => `共 ${total} 条记录`}
                />
              </div>
            )}
                  </Card>
                  </Form.Item>
                </Col>
              </Row>
          </Form>
        </Spin>
      </Modal>

      {/* 直播详情抽屉 */}
      <Drawer
        title="直播详情"
        placement="right"
        width={600}
        onClose={() => setDetailDrawerVisible(false)}
        open={detailDrawerVisible}
      >
        {selectedStream && (
          <>
            {/* 视频信息表格 */}
            <Card title="视频信息" size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="视频名称">{selectedStream.videoFile}</Descriptions.Item>
                
                {selectedStream.teacherName && (
                  <Descriptions.Item label="讲师">{selectedStream.teacherName}</Descriptions.Item>
                )}
                
                {selectedStream.courseName && (
                  <Descriptions.Item label="课程">{selectedStream.courseName}</Descriptions.Item>
                )}
                
                <Descriptions.Item label="视频时长">
                  {formatDuration(selectedStream.duration)}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 直播信息表格 */}
            <Card title="直播信息" size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={1} bordered size="small">
                {/* 课堂名称 */}
                <Descriptions.Item label="课堂名称">
                  {selectedStream.title || '-'}
                </Descriptions.Item>
                
                {/* 课堂简介 */}
                <Descriptions.Item label="课堂简介">
                  <div style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
                    {selectedStream.description || '-'}
                  </div>
                </Descriptions.Item>
                
                {/* 老师称呼 */}
                <Descriptions.Item label="老师称呼">
                  {selectedStream.teacher_real_name || '-'}
                </Descriptions.Item>
                
                {/* 老师简介 */}
                <Descriptions.Item label="老师简介">
                  <div style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
                    {selectedStream.teacher_desc || '-'}
                  </div>
                </Descriptions.Item>
                
                {/* 开始时间 */}
                <Descriptions.Item label="开始时间">
                  <div>
                    <div style={{ marginBottom: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>计划:</Text>
                      <br />
                      <Text style={{ fontSize: '13px' }}>
                        {selectedStream.startTime ? new Date(selectedStream.startTime).toLocaleString() : '-'}
                      </Text>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>实际:</Text>
                      <br />
                      <Text style={{ fontSize: '13px' }}>
                        {selectedStream.actualStartTime ? new Date(selectedStream.actualStartTime).toLocaleString() : '-'}
                      </Text>
                    </div>
                  </div>
                </Descriptions.Item>
                
                {/* 结束时间 */}
                <Descriptions.Item label="结束时间">
                  <div>
                    <div style={{ marginBottom: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>计划:</Text>
                      <br />
                      <Text style={{ fontSize: '13px' }}>
                        {selectedStream.endTime ? new Date(selectedStream.endTime).toLocaleString() : '手动结束'}
                      </Text>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>实际:</Text>
                      <br />
                      <Text style={{ fontSize: '13px' }}>
                        {selectedStream.actualEndTime ? new Date(selectedStream.actualEndTime).toLocaleString() : '-'}
                      </Text>
                    </div>
                  </div>
                </Descriptions.Item>
                
                {/* 直播时长 */}
                <Descriptions.Item label="直播时长">
                  {formatDuration(selectedStream.actualDuration || 0)}
                </Descriptions.Item>
                
                {/* 伯索课堂ID */}
                <Descriptions.Item label="伯索课堂ID">
                  {selectedStream.liveclassId || '-'}
                </Descriptions.Item>
                
                {/* 上课班级 */}
                {(selectedStream.group_mapping || selectedStream.groupName) && (
                  <Descriptions.Item label="上课班级">
                    {(() => {
                      try {
                        // 优先使用group_mapping字段
                        if (selectedStream.group_mapping) {
                          const groupMapping = JSON.parse(selectedStream.group_mapping);
                          if (Array.isArray(groupMapping) && groupMapping.length > 0) {
                            return (
                              <div>
                                {groupMapping.map((group, index) => (
                                  <Tag key={index} color="blue" style={{ marginBottom: '4px' }}>
                                    {group.name} (ID: {group.id})
                                  </Tag>
                                ))}
                              </div>
                            );
                          }
                        }
                        
                        // 回退到原来的groupName字段
                        if (selectedStream.groupName) {
                          const groupNames = JSON.parse(selectedStream.groupName);
                          if (Array.isArray(groupNames) && groupNames.length > 0) {
                            return (
                              <div>
                                {groupNames.map((name, index) => (
                                  <Tag key={index} color="blue" style={{ marginBottom: '4px' }}>
                                    {name}
                                  </Tag>
                                ))}
                              </div>
                            );
                          }
                        }
                      } catch (e) {
                        // 如果解析失败，直接显示原始字符串
                        return selectedStream.group_mapping || selectedStream.groupName;
                      }
                      return '-';
                    })()}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
            
            {/* 外链管理区域 */}
            <Card title="外链管理" size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Space>
                  {selectedStream.liveclassId ? (
                    selectedStream.external_link_enabled ? (
                      <Button 
                        type="primary" 
                        danger
                        icon={<DisconnectOutlined />}
                        onClick={() => handleCloseExternalLink(selectedStream.id)}
                      >
                        关闭外链
                      </Button>
                    ) : (
                      <Button 
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => handleEnableExternalLink(selectedStream.id)}
                      >
                        开启外链
                      </Button>
                    )
                  ) : (
                    <Button disabled>
                      无伯索课堂ID，无法操作外链
                    </Button>
                  )}
                  
                  <Tag color={selectedStream.external_link_enabled ? 'green' : 'default'}>
                    {selectedStream.external_link_enabled ? '外链已开启' : '外链未开启'}
                  </Tag>
                </Space>
              </div>
              
              {/* 外链信息 */}
              {selectedStream.external_link_enabled && (
                <Descriptions column={1} bordered size="small">
                  {selectedStream.live_url && (
                    <Descriptions.Item label="观看链接">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <Text 
                          style={{ flex: 1, wordBreak: 'break-all', minWidth: '200px' }}
                          copyable={{ text: selectedStream.live_url }}
                        >
                          {selectedStream.live_url}
                        </Text>
                        <Space>
                          <Button 
                            type="link" 
                            size="small"
                            onClick={() => window.open(selectedStream.live_url, '_blank')}
                          >
                            打开链接
                          </Button>
                          <Button 
                            type="primary"
                            size="small"
                            icon={<QrcodeOutlined />}
                            onClick={() => generateQRCode(selectedStream.live_url)}
                          >
                            生成二维码
                          </Button>
                        </Space>
                      </div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )}
            </Card>
            
            {/* 潜在学生管理区域 */}
            <Card title="潜在学生管理" size="small" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <Button 
                    type="primary"
                    icon={<DownloadOutlined />}
                    loading={fetchingVisitorInfo}
                    onClick={() => handleFetchVisitorInfo(selectedStream.id, selectedStream.liveclassId)}
                    disabled={!selectedStream.liveclassId}
                  >
                    获取潜在学生
                  </Button>
                  
                  <Tag color={visitorInfo.length > 0 ? 'green' : 'default'}>
                    {visitorInfo.length > 0 ? `已获取${visitorInfo.length}条潜在学生` : '暂无潜在学生'}
                  </Tag>
                  
                  {!selectedStream.liveclassId && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      需要伯索课堂ID才能获取潜在学生
                    </Text>
                  )}
                </Space>
              </div>
              
              {/* 潜在学生列表 */}
              {visitorInfo.length > 0 && (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table
                    dataSource={visitorInfo}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    loading={visitorLoading}
                    columns={[
                      {
                        title: '姓名',
                        dataIndex: 'username',
                        key: 'username',
                        width: 80,
                        sorter: (a, b) => {
                          const nameA = a.username || '';
                          const nameB = b.username || '';
                          return nameA.localeCompare(nameB, 'zh-CN');
                        },
                        render: (text) => text || '-'
                      },
                      {
                        title: '手机号',
                        dataIndex: 'phone',
                        key: 'phone',
                        width: 110,
                        render: (text) => text || '-'
                      },
                      {
                        title: '城市',
                        dataIndex: 'city',
                        key: 'city',
                        width: 80,
                        sorter: (a, b) => {
                          const cityA = a.city || '';
                          const cityB = b.city || '';
                          return cityA.localeCompare(cityB, 'zh-CN');
                        },
                        defaultSortOrder: 'ascend',
                        render: (text) => text || '-'
                      },
                      {
                        title: '届次',
                        dataIndex: 'grade',
                        key: 'grade',
                        width: 80,
                        sorter: (a, b) => {
                          const gradeA = a.grade || '';
                          const gradeB = b.grade || '';
                          return gradeA.localeCompare(gradeB, 'zh-CN');
                        },
                        render: (text) => text || '-'
                      }
                    ]}
                  />
                </div>
              )}
              
              {visitorInfo.length === 0 && !visitorLoading && (
                <Empty 
                  description="暂无潜在学生" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '20px 0' }}
                />
              )}
            </Card>
            
            <Divider />
          </>
        )}
      </Drawer>

      {/* 二维码显示Modal */}
      <Modal
        title={null}
        open={qrCodeModalVisible}
        onCancel={() => setQrCodeModalVisible(false)}
        footer={null}
        width={500}
        centered
        styles={{
          body: { padding: 0 }
        }}
        closeIcon={
          <div style={{
            position: 'absolute',
            top: '-40px',
            right: '-40px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '16px',
            cursor: 'pointer',
            zIndex: 1000
          }}>
            ×
          </div>
        }
      >
        {selectedStream && (
          <div style={{
            background: 'linear-gradient(135deg, #1f4e3d 0%, #0f2419 100%)',
            color: 'white',
            padding: '0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {/* 海报头部 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.08)',
              padding: '32px 24px',
              textAlign: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
              position: 'relative'
            }}>
              {/* 状态标签 */}
              <div style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: selectedStream.status === '推流中' ? '#52c41a' : 
                           selectedStream.status === '等待开始' ? '#faad14' : '#d9d9d9',
                color: 'white',
                padding: '6px 14px',
                borderRadius: '15px',
                fontSize: '12px',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                {selectedStream.status || '未知状态'}
              </div>
              
              {/* 品牌标题 */}
              <div style={{
                fontSize: '36px',
                fontWeight: '900',
                marginBottom: '12px',
                textShadow: '0 3px 6px rgba(0,0,0,0.5)',
                color: '#fff',
                letterSpacing: '2px',
                fontFamily: 'Microsoft YaHei, SimHei, Arial, sans-serif'
              }}>
                同方专转本
              </div>
              
              <div style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '16px',
                color: '#e6f7ff',
                letterSpacing: '1px'
              }}>
                公开课
              </div>
              
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '8px',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                lineHeight: '1.3',
                color: '#f0f0f0'
              }}>
                {selectedStream.title || selectedStream.videoFile}
              </div>
              <div style={{
                fontSize: '16px',
                opacity: 0.9,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                {selectedStream.teacher_real_name && (
                  <span style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '14px'
                  }}>
                    <i className="anticon anticon-user" style={{ marginRight: '4px' }} />
                    讲师：{selectedStream.teacher_real_name}
                  </span>
                )}
              </div>
            </div>

            {/* 二维码区域 */}
            <div style={{
              padding: '40px 32px',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              margin: '24px',
              borderRadius: '16px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
            }}>
              {qrCodeUrl && (
                <>
                  <div style={{
                    display: 'inline-block',
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                  }}>
                    <Image
                      src={qrCodeUrl}
                      alt="直播观看二维码"
                      style={{ 
                        width: '200px', 
                        height: '200px',
                        display: 'block'
                      }}
                      preview={false}
                    />
                  </div>
                  <div style={{
                    marginTop: '20px',
                    color: '#1f4e3d',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    扫描二维码观看直播
                  </div>
                  <div style={{
                    marginTop: '8px',
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    使用手机扫码即可进入直播间
                  </div>
                </>
              )}
            </div>

            {/* 直播信息区域 */}
            <div style={{
              padding: '24px',
              background: 'rgba(255, 255, 255, 0.08)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '24px'
            }}>
              {selectedStream.startTime && (
                <div style={{ 
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.12)',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  minWidth: '160px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '6px' }}>
                    开始时间
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>
                    {new Date(selectedStream.startTime).toLocaleString()}
                  </div>
                </div>
              )}
              
              {selectedStream.duration && (
                <div style={{
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.12)',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  minWidth: '120px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '6px' }}>
                    课程时长
                  </div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold'
                  }}>
                    {formatRoundedDuration(selectedStream.duration)}
                  </div>
                </div>
              )}
            </div>

            {/* 底部装饰 */}
            <div style={{
              height: '6px',
              background: 'linear-gradient(90deg, #1f4e3d, #2d5a47, #3b6651, #49725b, #577e65)'
            }} />
          </div>
        )}
      </Modal>
    </div>
  );
};

// 使用App组件包裹LiveSimList
export default () => (
  <App>
    <LiveSimList />
  </App>
); 